import React from 'react';
import { Gift, Info, AlertTriangle, AlertCircle, X } from 'lucide-react';

/**
 * SiteMessage Component
 *
 * Displays admin-configured site messages with different styles and variants.
 * Supports dismissal, CTAs, and various message types.
 *
 * @param {Object} message - Message object from database
 * @param {string} variant - 'full' or 'compact' display
 * @param {Function} onDismiss - Callback when message is dismissed
 * @param {string} className - Additional CSS classes
 */
const SiteMessage = ({
  message,
  variant = 'full',
  onDismiss,
  className = ''
}) => {
  // Map message types to visual styles
  const styles = {
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      text: 'text-green-800 dark:text-green-200',
      mutedText: 'text-green-700 dark:text-green-300',
      icon: 'text-green-600 dark:text-green-400',
      IconComponent: Gift
    },
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-800 dark:text-blue-200',
      mutedText: 'text-blue-700 dark:text-blue-300',
      icon: 'text-blue-600 dark:text-blue-400',
      IconComponent: Info
    },
    warning: {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      border: 'border-amber-200 dark:border-amber-800',
      text: 'text-amber-800 dark:text-amber-200',
      mutedText: 'text-amber-700 dark:text-amber-300',
      icon: 'text-amber-600 dark:text-amber-400',
      IconComponent: AlertTriangle
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      text: 'text-red-800 dark:text-red-200',
      mutedText: 'text-red-700 dark:text-red-300',
      icon: 'text-red-600 dark:text-red-400',
      IconComponent: AlertCircle
    }
  };

  const style = styles[message.message_type] || styles.info;
  const Icon = style.IconComponent;

  const handleCtaClick = () => {
    // Track CTA click if analytics is enabled
    if (window.trackMessageCtaClick) {
      window.trackMessageCtaClick(message.id);
    }
  };

  // Compact variant - single line with icon
  if (variant === 'compact') {
    return (
      <div className={`${style.bg} border ${style.border} rounded-lg p-3 ${className} relative`}>
        <div className="flex items-start gap-2">
          <Icon size={18} className={`${style.icon} flex-shrink-0 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm ${style.text}`}>
              {message.title && <strong>{message.title}: </strong>}
              {message.content}
            </p>
            {message.cta_text && message.cta_link && (
              <a
                href={message.cta_link}
                onClick={handleCtaClick}
                className={`text-sm font-medium ${style.icon} hover:underline mt-1 inline-block`}
              >
                {message.cta_text} →
              </a>
            )}
          </div>
          {message.dismissible && onDismiss && (
            <button
              onClick={() => onDismiss(message.id)}
              className={`${style.icon} hover:opacity-70 flex-shrink-0 transition-opacity`}
              aria-label="Dismiss message"
              type="button"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Full variant - detailed with title and multi-line content
  return (
    <div className={`${style.bg} border ${style.border} rounded-lg p-4 ${className} relative`}>
      <div className="flex items-start gap-3">
        <Icon size={24} className={`${style.icon} flex-shrink-0 mt-1`} />
        <div className="flex-1 min-w-0 space-y-2">
          {message.title && (
            <h3 className={`text-base font-semibold ${style.text}`}>
              {message.title}
            </h3>
          )}
          <p className={`text-sm ${style.mutedText} whitespace-pre-wrap`}>
            {message.content}
          </p>
          {message.cta_text && message.cta_link && (
            <a
              href={message.cta_link}
              onClick={handleCtaClick}
              className={`text-sm font-medium ${style.icon} hover:underline inline-flex items-center gap-1 transition-colors`}
            >
              {message.cta_text} →
            </a>
          )}
        </div>
        {message.dismissible && onDismiss && (
          <button
            onClick={() => onDismiss(message.id)}
            className={`${style.icon} hover:opacity-70 flex-shrink-0 transition-opacity`}
            aria-label="Dismiss message"
            type="button"
          >
            <X size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SiteMessage;
